import React, {useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {Add, Delete, Edit} from "@mui/icons-material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_ADMIN_EXPORTER_SETS} from "../../components/graphql/queries/gqlAdminSettings";
import {DELETE_ADMIN_EXPORTER_SET, SAVE_ADMIN_EXPORTER_SET} from "../../components/graphql/mutations/gqlAdminMutations";
import {EXPORTER_TYPE_MAP, EXPORTER_TYPE_OPTIONS, ExporterSet} from "../adminTypes";
import {SettingRowEditor, SettingRowData} from "./SettingRowEditor";

export function ExporterSetsTab() {
	const [sets, setSets] = useState<ExporterSet[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editingSet, setEditingSet] = useState<ExporterSet | null>(null);
	const [saving, setSaving] = useState(false);

	const loadSets = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_EXPORTER_SETS,
				fetchPolicy: "no-cache",
			});
			setSets((result.data.adminExporterSets ?? []).map((s: any) => ({
				...s,
				exporters: (s.exporters ?? []).map((e: any) => ({
					...e,
					exporterType: typeof e.exporterType === "string"
						? (EXPORTER_TYPE_MAP[e.exporterType] ?? e.exporterType)
						: e.exporterType,
				})),
			})));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadSets(); 
	}, []);

	const handleEdit = (set: ExporterSet) => setEditingSet(JSON.parse(JSON.stringify(set)));
	const handleNew = () => setEditingSet({id: 0, code: "", description: "", exporters: []});

	const handleDelete = async (id: number) => {
		if (!window.confirm("Delete this exporter set?")) return;
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: DELETE_ADMIN_EXPORTER_SET,
				variables: {input: {id}},
			});
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		}
	};

	const handleSave = async () => {
		if (!editingSet) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_EXPORTER_SET,
				variables: {
					input: {
						id: editingSet.id || undefined,
						code: editingSet.code,
						description: editingSet.description,
						exporters: editingSet.exporters.map(e => ({
							id: e.id || undefined,
							order: e.order,
							code: e.code,
							exporterType: e.exporterType,
							settings: e.settings,
							description: e.description,
						})),
					},
				},
			});
			setEditingSet(null);
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const updateRow = (idx: number, updated: SettingRowData) => {
		if (!editingSet) return;
		const exporters = [...editingSet.exporters];
		exporters[idx] = {
			...exporters[idx],
			order: updated.order,
			code: updated.code ?? null,
			exporterType: updated.type,
			settings: updated.settings,
			description: updated.description,
		};
		setEditingSet({...editingSet, exporters});
	};

	if (loading) return <CircularProgress />;

	return (
		<Box sx={{mt: 2}}>
			{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			<Button startIcon={<Add />} variant="contained" onClick={handleNew} sx={{mb: 2}}>
				Add Exporter Set
			</Button>
			{sets.map(set => (
				<Card key={set.id} sx={{mb: 2}}>
					<CardContent>
						<Typography variant="h6">{set.code}</Typography>
						<Typography variant="body2" color="text.secondary">{set.description}</Typography>
						<Typography variant="caption">{set.exporters.length} exporter(s)</Typography>
					</CardContent>
					<CardActions>
						<IconButton onClick={() => handleEdit(set)}><Edit /></IconButton>
						<IconButton color="error" onClick={() => handleDelete(set.id)}><Delete /></IconButton>
					</CardActions>
				</Card>
			))}

			<Dialog open={!!editingSet} onClose={() => setEditingSet(null)} maxWidth="lg" fullWidth>
				<DialogTitle>{editingSet?.id ? "Edit Exporter Set" : "New Exporter Set"}</DialogTitle>
				<DialogContent>
					{editingSet && (
						<Box sx={{mt: 1}}>
							{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
							<TextField fullWidth label="Code" value={editingSet.code}
								onChange={e => setEditingSet({...editingSet, code: e.target.value})}
								sx={{mb: 2}} />
							<TextField fullWidth label="Description" value={editingSet.description}
								onChange={e => setEditingSet({...editingSet, description: e.target.value})}
								sx={{mb: 2}} />
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Code</TableCell>
										<TableCell>Description</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{[...editingSet.exporters]
										.map((exp, originalIdx) => ({exp, originalIdx}))
										.sort((a, b) => a.exp.order - b.exp.order)
										.map(({exp, originalIdx}) => (
											<SettingRowEditor
												key={exp.id || `new-${originalIdx}`}
												setting={{
													id: exp.id,
													order: exp.order,
													type: exp.exporterType,
													code: exp.code ?? undefined,
													settings: exp.settings,
													description: exp.description,
												}}
												typeOptions={EXPORTER_TYPE_OPTIONS}
												showCode
												onChange={updated => updateRow(originalIdx, updated)}
												onDelete={() => setEditingSet({
													...editingSet,
													exporters: editingSet.exporters.filter((_, i) => i !== originalIdx),
												})}
											/>
										))}
								</TableBody>
							</Table>
							<Button startIcon={<Add />} sx={{mt: 1}} onClick={() =>
								setEditingSet({
									...editingSet,
									exporters: [...editingSet.exporters, {
										id: 0, order: editingSet.exporters.length + 1,
										code: null, exporterType: 0, settings: "{}", description: null,
									}],
								})
							}>
								Add Exporter
							</Button>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditingSet(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}